/**
 * 前の描き手（版 1。#217 より前の自分の書式と、領収書の自分の書式）。#217 からの見積書・請求書は engine.ts（版 2）。
 * 出した紙は出したときの描き手で描く（snapshot に版が無ければ版 1）＝この関数は消さない・形を変えない。
 * 書式（PaperLayout）＋データ（PaperData）→ 紙の HTML。
 *   ・座標は mm のまま CSS に書く（Chromium の印刷は mm を正確に扱う）。画面のエディタは外側を transform: scale で縮める
 *   ・ページ：1ページ目は全部の部品＋明細表。行が入りきらなければ 2ページ目以降（secondPage＝compact：番号・日付・ページ番号と表だけ／same：同じ部品）。
 *     「明細表の下に付けて動かす」部品（dock）は最後のページの表の下。入りきらなければ行を減らして次のページへ送る（paginate）
 *   ・専用帳票モード（page.preprinted）：枠の部品（frame）と明細の罫線・見出し・地色を出さず、値だけ
 *   ・印字位置の補正（page.offset）は印刷のときだけ（mode=print）
 *   ・mode=edit：1ページ目だけ、部品に data-id を付け、印字しない部品も薄く出す（エディタが選ぶため）。透かし無し
 *   ・画像（046）：assets（画像の id → URL か data: URL）で実体を受け取る。下敷き（guide）は edit のときだけ。二次元コードは SVG を埋める
 */
import type { Labels, PaperCol, PaperData, PaperEl, PaperLayout, PaperRow, PaperRule, PaperTable } from "@genbaryoku/contract";
import { FONT_CSS, type RenderMode } from "./engine.js";
import { esc, nl, r1 } from "./fmt.js";
import { qrContent, qrSvg } from "./qr.js";

/** assets：画像の id（paper_images の id か company:logo／company:seal）→ 画像の URL（PDF は data: URL、エディタは API の URL）。無い id は描かない */
/** labels：エディタの紙で値が空の項目に出す見本の名前（［工事名］など）を会社の言葉に（#142 段6 No.35）。印刷には出ない */
export type RenderOptions = { mode?: RenderMode; assets?: Record<string, string>; labels?: Labels };

/** 明細表の高さ（見出し＋行数×行間）。行数を指定するので固定 */
export function tableHeight(t: PaperTable, rows: number = t.rows): number {
  return (t.header.on ? t.header.h : 0) + rows * t.pitch;
}
/** dock の部品の Y（表の下端＋3mm から dy） */
export function dockY(t: PaperTable, rows: number, dy: number): number {
  return t.y + tableHeight(t, rows) + 3 + dy;
}
/** 部品の紙の上の位置（dock なら表の下）。編集画面の当たり判定にも使う */
export function elBox(e: PaperEl, t: PaperTable, tableY: number = t.y, rows: number = t.rows): { x: number; y: number; w: number; h: number } {
  const y = e.dock === "table" ? tableY + tableHeight(t, rows) + 3 + (e.dy ?? 0) : e.y;
  if (e.type === "line") return e.dir === "v" ? { x: e.x, y, w: 0, h: e.h } : { x: e.x, y, w: e.w, h: 0 };
  return { x: e.x, y, w: e.w, h: e.h };
}
/** dock の部品がぜんぶ入るのに要る高さ（表の下端＋3mm から） */
export function dockHeight(layout: PaperLayout): number {
  let h = 0;
  for (const e of layout.els) {
    if (e.dock !== "table" || !e.on) continue;
    const b = e.type === "line" && e.dir !== "v" ? 0 : e.h;
    h = Math.max(h, (e.dy ?? 0) + b);
  }
  return h;
}

export type PaperPageSpec = { rows: PaperRow[]; cap: number; tableY: number; first: boolean; last: boolean };

/**
 * 行をページに割る。1ページ目は table.rows 行。2ページ目以降は 紙の下の余白まで（compact なら表を上に寄せる）。
 * 最後のページは dock の部品が入る分だけ行を減らす（入らなければ、次のページを dock だけにする）
 */
export function paginate(layout: PaperLayout, rows: PaperRow[]): PaperPageSpec[] {
  const t = layout.table;
  const pg = layout.page;
  const bottom = pg.h - pg.margin[2];
  const dockH = dockHeight(layout);
  const headH = t.header.on ? t.header.h : 0;
  // 2ページ目以降の表の位置
  let tableY2 = t.y;
  if (pg.secondPage === "compact") {
    let keepBottom = pg.margin[0];
    for (const e of compactEls(layout)) {
      if (e.dock === "table" || !e.on) continue;
      const b = elBox(e, t);
      // 表より上にある部品（番号・日付）だけ見る。ページ番号のように紙の下にある部品は数えない
      if (b.y + b.h > t.y) continue;
      keepBottom = Math.max(keepBottom, b.y + b.h);
    }
    tableY2 = Math.max(pg.margin[0], r1(keepBottom + 3));
  }
  const capFull = Math.max(1, Math.floor((bottom - tableY2 - headH) / t.pitch));
  const capLast = Math.floor((bottom - tableY2 - headH - 3 - dockH) / t.pitch);

  const pages: PaperPageSpec[] = [];
  let i = 0;
  // 1ページ目
  const firstCap = t.rows;
  const firstRows = rows.slice(0, firstCap);
  i = firstRows.length;
  if (i >= rows.length) return [{ rows: firstRows, cap: firstCap, tableY: t.y, first: true, last: true }];
  pages.push({ rows: firstRows, cap: firstCap, tableY: t.y, first: true, last: false });
  // 2ページ目以降
  let guard = 0;
  while (i < rows.length && guard++ < 500) {
    const remaining = rows.length - i;
    if (capLast >= 1 && remaining <= capLast) {
      // 最後のページの表は、その行数ぶんだけ（明細の下の部品がすぐ下に来る）
      pages.push({ rows: rows.slice(i), cap: remaining, tableY: tableY2, first: false, last: true });
      i = rows.length;
      break;
    }
    const take = Math.min(capFull, remaining);
    pages.push({ rows: rows.slice(i, i + take), cap: capFull, tableY: tableY2, first: false, last: false });
    i += take;
  }
  const lastPage = pages[pages.length - 1]!;
  if (!lastPage.last) {
    // dock が入るページが無い → dock だけのページ（表は空）
    pages.push({ rows: [], cap: Math.max(0, capLast), tableY: tableY2, first: false, last: true });
  }
  return pages;
}

/** compact の2ページ目以降に残す部品：ページ番号と、番号・日付（とその組） */
function compactEls(layout: PaperLayout): PaperEl[] {
  const groups = new Set<string>();
  for (const e of layout.els) if (e.type === "field" && (e.bind === "docNo" || e.bind === "docDate") && e.group) groups.add(e.group);
  return layout.els.filter((e) => (e.type === "field" && (e.bind === "pageNo" || e.bind === "docNo" || e.bind === "docDate")) || (e.group !== undefined && groups.has(e.group)));
}

const mm = (v: number): string => `${r1(v)}mm`;
const pt = (v: number): string => `${v}pt`;

function ruleCss(r: PaperRule | undefined, side: "top" | "bottom" | "left" | "right"): string {
  if (!r || r.style === "none" || !r.w) return `border-${side}:0;`;
  const w = r.style === "double" ? Math.max(r.w, 0.75) : r.w;
  return `border-${side}:${w}mm ${r.style} ${r.color};`;
}

function textStyle(e: { font: "serif" | "sans" | "maru"; size: number; bold: boolean; align: "left" | "center" | "right"; valign: "top" | "middle" | "bottom"; color: string }): string {
  const va = { top: "flex-start", middle: "center", bottom: "flex-end" }[e.valign];
  const ha = { left: "flex-start", center: "center", right: "flex-end" }[e.align];
  return `font-family:${FONT_CSS[e.font]};font-size:${pt(e.size)};font-weight:${e.bold ? 700 : 400};color:${e.color || "#1A1A18"};align-items:${va};justify-content:${ha};text-align:${e.align};`;
}

function elHtml(e: PaperEl, layout: PaperLayout, page: PaperPageSpec, data: PaperData, pageNo: number, pageCount: number, mode: RenderMode, assets: Record<string, string>, labels?: Labels): string {
  const t = layout.table;
  const pre = layout.page.preprinted;
  const b = elBox(e, t, page.tableY, page.cap);
  // 下敷き（guide）は印刷しない（テスト印刷にも出ない）。エディタだけ
  const hidden = !e.on || (pre && e.frame) || (e.type === "image" && e.guide);
  if (hidden && mode === "print") return "";
  const attrs = mode === "edit" ? ` data-id="${esc(e.id)}" data-type="${e.type}"${hidden ? ' data-off="1"' : ""}` : "";
  const dim = hidden ? "opacity:.28;outline:0.3mm dashed #9E2718;" : "";
  const pos = `position:absolute;left:${mm(b.x)};top:${mm(b.y)};width:${mm(Math.max(b.w, 0.1))};height:${mm(Math.max(b.h, 0.1))};`;
  switch (e.type) {
    case "text":
      return `<div class="el"${attrs} style="${pos}${dim}display:flex;overflow:hidden;line-height:1.35;padding:0 0.5mm;${textStyle(e)}"><span>${nl(e.text)}</span></div>`;
    case "field": {
      const raw = e.bind === "pageNo" ? `${pageNo} / ${pageCount}` : (data.fields[e.bind] ?? "");
      // 前後に付ける文字（#216：「¥」「－」「No. 」）。値が空なら付けない
      const v = raw === "" ? "" : `${e.prefix ?? ""}${raw}${e.suffix ?? ""}`;
      const show = mode === "edit" && v === "" ? `<span style="color:#9E2718;opacity:.6">${esc(bindLabelShort(e.bind, labels))}</span>` : `<span>${nl(v)}</span>`;
      return `<div class="el"${attrs} style="${pos}${dim}display:flex;overflow:hidden;line-height:1.35;padding:0 0.5mm;${textStyle(e)}">${show}</div>`;
    }
    case "line": {
      const rule = { style: e.style, w: e.lw, color: e.color };
      const css = e.dir === "v" ? `${ruleCss(rule, "left")}width:0;height:${mm(e.h)};` : `${ruleCss(rule, "top")}height:0;width:${mm(e.w)};`;
      return `<div class="el"${attrs} style="position:absolute;left:${mm(b.x)};top:${mm(b.y)};${css}${dim}"></div>`;
    }
    case "box": {
      const border = e.style === "none" ? "border:0;" : `border:${(e.style === "double" ? Math.max(e.lw, 0.75) : e.lw)}mm ${e.style} ${e.color};`;
      return `<div class="el"${attrs} style="${pos}${dim}${border}background:${e.fill || "transparent"};"></div>`;
    }
    case "image": {
      const src = e.asset ? assets[e.asset] : undefined;
      if (!src) {
        // 画像が選ばれていない・無い：エディタでは枠と名前、印刷では何も出さない
        const why = e.asset?.startsWith("company:") ? "（自社情報に登録すると出ます）" : "（画像を選んでください）";
        return mode === "edit" ? `<div class="el"${attrs} style="${pos}${dim}border:0.3mm dashed #8C8880;display:flex;align-items:center;justify-content:center;font-size:7pt;color:#55534C;text-align:center">${esc(e.name || "画像")}<br>${why}</div>` : "";
      }
      const fit = e.keep ? "object-fit:contain;object-position:left top;" : "object-fit:fill;";
      const guideMark = e.guide && mode === "edit" ? `<span style="position:absolute;left:0;top:0;font-size:6.5pt;color:#9E2718;background:rgba(255,255,255,.8);padding:0 1mm;line-height:1.4">下敷き（印刷しません）</span>` : "";
      // 下敷きは紙を覆うので、エディタでは紙の上から選べない（一覧から選ぶ。data-guide をエディタが pointer-events:none にする）
      return `<div class="el img"${attrs}${e.guide && mode === "edit" ? ' data-guide="1"' : ""} style="${pos}${dim}overflow:hidden;"><img src="${esc(src)}" alt="" draggable="false" style="display:block;width:100%;height:100%;${fit}opacity:${e.opacity};">${guideMark}</div>`;
    }
    case "qr": {
      const content = qrContent(e, data.fields);
      const size = Math.min(b.w, b.h);
      const svg = content ? qrSvg(content, size) : "";
      if (!svg) return mode === "edit" ? `<div class="el"${attrs} style="${pos}${dim}border:0.3mm dashed #8C8880;display:flex;align-items:center;justify-content:center;font-size:7pt;color:#55534C;text-align:center">二次元コード<br>（中身を入れてください）</div>` : "";
      return `<div class="el qr"${attrs} style="${pos}${dim}overflow:hidden;background:#fff;">${svg}</div>`;
    }
  }
}

const SHORT: Record<string, string> = {
  docNo: "番号", docDate: "日付", toName: "宛名", toSub: "宛名の担当者", projectName: "工事名", site: "工事場所", period: "工期", outline: "工事概要", terms: "支払条件", valid: "有効期限", due: "お支払期限", staff: "担当",
  coName: "会社名", coRep: "代表者名", coAddr: "住所", coTel: "電話", coReg: "登録番号", coPerson: "自社担当", subtotal: "小計", tax: "消費税", total: "合計", taxByRate: "税率ごとの内訳", notes: "備考", bank: "振込先", installment: "分割の欄", pageNo: "ページ",
  proviso: "但し書き", invoiceNo: "請求書番号",
};
/** 見本の短い名前。{subject} などの4つは会社の言葉（省けば現場力の言葉） */
const bindLabelShort = (b: string, labels?: Labels): string => {
  const own = labels ? ({ projectName: labels.subject, site: labels.site, period: labels.period, outline: labels.outline } as Record<string, string>)[b] : undefined;
  return `［${own ?? SHORT[b] ?? b}］`;
};

function tableHtml(layout: PaperLayout, page: PaperPageSpec, mode: RenderMode, taxNote?: string): string {
  const t = layout.table;
  const pre = layout.page.preprinted;
  if (page.cap === 0) return ""; // 明細の下の部品だけのページ
  const cols = t.cols.filter((c) => c.on);
  const headH = t.header.on ? t.header.h : 0;
  const H = headH + page.cap * t.pitch;
  const parts: string[] = [];
  const attrs = mode === "edit" ? ` data-id="table" data-type="table"` : "";
  const outer = pre ? "" : `${ruleCss(t.rules.outer, "top")}${ruleCss(t.rules.outer, "bottom")}${ruleCss(t.rules.outer, "left")}${ruleCss(t.rules.outer, "right")}`;
  parts.push(`<div class="el tbl"${attrs} style="position:absolute;left:${mm(t.x)};top:${mm(page.tableY)};width:${mm(t.w)};height:${mm(H)};${outer}box-sizing:border-box;">`);
  // 見出し行
  if (t.header.on && !pre) {
    parts.push(`<div style="position:absolute;left:0;top:0;width:100%;height:${mm(headH)};background:${t.header.fill || "transparent"};${ruleCss(t.rules.head, "bottom")}box-sizing:border-box;"></div>`);
    for (const c of cols) {
      parts.push(cell(c.x, 0, c.w, headH, c.label, { size: t.header.size, bold: t.header.bold, align: c.labelAlign, color: t.header.color }));
    }
  }
  // 行
  for (let i = 0; i < page.cap; i++) {
    const y = headH + i * t.pitch;
    const r = page.rows[i];
    if (!pre && t.zebra && i % 2 === 1) parts.push(`<div style="position:absolute;left:0;top:${mm(y)};width:100%;height:${mm(t.pitch)};background:${t.zebra};"></div>`);
    if (!pre && i > 0) parts.push(`<div style="position:absolute;left:0;top:${mm(y)};width:100%;height:0;${ruleCss(t.rules.h, "top")}"></div>`);
    if (r) parts.push(rowHtml(t, cols, r, y));
    else if (i === page.rows.length && page.last && t.restText) {
      const nameCol = cols.find((c) => c.id === "name") ?? cols[0]!;
      parts.push(cell(nameCol.x, y, nameCol.w, t.pitch, t.restText, { size: t.size, bold: false, align: "left", color: t.color }));
    }
  }
  // 列の間の線
  if (!pre) {
    for (let k = 1; k < cols.length; k++) {
      const c = cols[k]!;
      parts.push(`<div style="position:absolute;left:${mm(c.x)};top:0;width:0;height:100%;${ruleCss(t.rules.v, "left")}"></div>`);
    }
  }
  // 明細表の下の注記（T2：「※は軽減税率対象」）。表の部品の一部＝書式で消せない・部品は増やさない。
  // 表の下端と明細の下の部品（dock は表の下端＋3mm から）のあいだの 3mm に1行で描く。最後のページ（表の終わり）だけ
  if (taxNote && page.last) {
    parts.push(`<div class="tnote" style="position:absolute;left:0;top:${mm(H + 0.4)};width:100%;height:2.6mm;display:flex;align-items:center;overflow:hidden;font-family:${FONT_CSS.sans};font-size:7pt;line-height:1;color:${t.color || "#1A1A18"};padding:0 1mm;box-sizing:border-box;"><span style="white-space:nowrap">${esc(taxNote)}</span></div>`);
  }
  parts.push(`</div>`);
  return parts.join("");
}

function cell(x: number, y: number, w: number, h: number, text: string, s: { size: number; bold: boolean; align: "left" | "center" | "right"; color: string; font?: "serif" | "sans" | "maru"; valign?: "top" | "middle" | "bottom" }): string {
  // 明細の欄は1行（折り返さない。行間が決まっているので、はみ出す文字は切れる＝列の幅で調整する）
  return `<div style="position:absolute;left:${mm(x)};top:${mm(y)};width:${mm(w)};height:${mm(h)};display:flex;overflow:hidden;line-height:1.3;padding:0 1mm;box-sizing:border-box;${textStyle({ font: s.font ?? "sans", size: s.size, bold: s.bold, align: s.align, valign: s.valign ?? "middle", color: s.color })}"><span style="white-space:nowrap">${esc(text)}</span></div>`;
}

function rowHtml(t: PaperTable, cols: PaperCol[], r: PaperRow, y: number): string {
  const first = cols[0]!;
  const last = cols[cols.length - 1]!;
  const nameCol = cols.find((c) => c.id === "name");
  const amtCol = cols.find((c) => c.id === "amount");
  const spanFrom = nameCol ?? first;
  const spanW = last.x + last.w - spanFrom.x;
  switch (r.kind) {
    case "heading":
    case "sheet":
      return cell(spanFrom.x, y, spanW, t.pitch, r.name, { size: nameCol?.size ?? t.size, bold: true, align: "left", color: t.color });
    case "text":
      return cell(spanFrom.x, y, spanW, t.pitch, r.name, { size: Math.max(6, (nameCol?.size ?? t.size) - 1), bold: false, align: "left", color: t.color });
    case "subtotal":
    case "sheetTotal": {
      const labelW = amtCol ? amtCol.x - spanFrom.x : spanW;
      const out = [cell(spanFrom.x, y, labelW, t.pitch, r.name, { size: nameCol?.size ?? t.size, bold: r.kind === "sheetTotal", align: "right", color: t.color })];
      if (amtCol) out.push(cell(amtCol.x, y, amtCol.w, t.pitch, r.amount, { size: amtCol.size, bold: r.kind === "sheetTotal", align: amtCol.align, color: t.color }));
      return out.join("");
    }
    default:
      return cols.map((c) => cell(c.x, y, c.w, t.pitch, r[c.id] ?? "", { size: c.size, bold: false, align: c.align, color: t.color })).join("");
  }
}

/** 1ページの中身（.page の中） */
export function pageInner(layout: PaperLayout, data: PaperData, page: PaperPageSpec, pageNo: number, pageCount: number, mode: RenderMode, assets: Record<string, string> = {}, labels?: Labels): string {
  // compact の2ページ目以降：番号・日付・ページ番号＋表＋（最後のページなら）明細の下の部品
  const full = page.first || layout.page.secondPage === "same";
  const els = full ? layout.els : [...compactEls(layout), ...layout.els.filter((e) => e.dock === "table")];
  const z = (e: PaperEl): number => (e.type === "image" && e.guide ? 0 : e.type === "image" || e.type === "box" ? 1 : 3);
  const items: Array<{ z: number; html: string }> = [];
  for (const e of els) {
    if (e.dock === "table" && !page.last) continue;
    items.push({ z: z(e), html: elHtml(e, layout, page, data, pageNo, pageCount, mode, assets, labels) });
  }
  items.push({ z: 2, html: tableHtml(layout, page, mode, data.taxNote) });
  items.sort((a, b) => a.z - b.z);
  const wm = mode === "print" && data.watermark ? `<div class="wm">${esc(data.watermark)}</div>` : "";
  return items.map((x) => x.html).join("") + wm;
}

export function paperCss(layout: PaperLayout, mode: RenderMode): string {
  const pg = layout.page;
  return `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; color: #1A1A18; font-family: ${FONT_CSS.sans}; }
  .page { position: relative; width: ${mm(pg.w)}; height: ${mm(pg.h)}; overflow: hidden; background: #fff; }
  ${mode === "print" ? `@page { size: ${mm(pg.w)} ${mm(pg.h)}; margin: 0; } .page { page-break-after: always; break-after: page; } .page:last-child { page-break-after: auto; break-after: auto; }` : ""}
  .page .inner { position: absolute; left: 0; top: 0; width: 100%; height: 100%; ${mode === "print" && (pg.offset[0] || pg.offset[1]) ? `transform: translate(${mm(pg.offset[0])}, ${mm(pg.offset[1])});` : ""} }
  .el span { white-space: pre-wrap; overflow-wrap: anywhere; }
  .wm { position: absolute; top: 40%; left: 0; right: 0; text-align: center; font-size: 60pt; font-weight: 700; color: rgba(160, 40, 40, 0.16); transform: rotate(-20deg); letter-spacing: 0.3em; pointer-events: none; z-index: 10; }
`;
}

/** 紙全体の HTML（PDF・プレビュー用。版 1）。mode=edit は1ページ目だけ */
export function renderPaperHtmlV1(layout: PaperLayout, data: PaperData, opts: RenderOptions = {}): string {
  const mode = opts.mode ?? "print";
  const { pages } = renderPagesV1(layout, data, opts);
  return `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${esc(layout.name)}</title><style>${paperCss(layout, mode)}</style></head><body>${pages.map((p) => `<div class="page"><div class="inner">${p}</div></div>`).join("")}</body></html>`;
}

/** ページごとの中身（.page の中の HTML。版 1）。エディタは1ページ目だけ受け取って自分の枠に入れる */
export function renderPagesV1(layout: PaperLayout, data: PaperData, opts: RenderOptions = {}): { pages: string[]; specs: PaperPageSpec[] } {
  const mode = opts.mode ?? "print";
  const specs = mode === "edit" ? [{ rows: data.rows.slice(0, layout.table.rows), cap: layout.table.rows, tableY: layout.table.y, first: true, last: true }] : paginate(layout, data.rows);
  const pages = specs.map((p, i) => pageInner(layout, data, p, i + 1, specs.length, mode, opts.assets ?? {}, opts.labels));
  return { pages, specs };
}
